import React, { useState } from "react";
import { Box, Switch, TextField, Typography, ToggleButton, ToggleButtonGroup, IconButton, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { toast } from "react-toastify";
import { compressImage } from "../../../../../../../utils/imageCompressor";

const glassCard = { p: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)", border: "1px solid rgba(229,231,235,0.6)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" };
const accentText = (a) => ({ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 }, "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: a }, "& .MuiInputLabel-root.Mui-focused": { color: a } });

const SingleChoiceTab = ({
  skipOption, setSkipOption,
  errorMessage, setErrorMessage,
  options, setOptions,
  flexDirection, setFlexDirection,
  style, setStyle,
  defaultSelected, setDefaultSelected,
  shuffleOptions, setShuffleOptions,
  otherOption, setOtherOption,
  imageChoices, setImageChoices,
  accentColor = "#6366f1",
}) => {
  const optionsList = options || ["Option 1", "Option 2"];
  const optText = (o) => (typeof o === "object" ? (o?.label || o?.value || "") : (o || ""));

  const handleAddOption = () => setOptions([...optionsList, ""]);
  const handleChangeOption = (i, val) => { const u = [...optionsList]; u[i] = val; setOptions(u); };
  const handleDeleteOption = (i) => {
    const next = optionsList.filter((_, idx) => idx !== i);
    setOptions(next.length ? next : [""]);
  };

  const handleImageUpload = (i) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const compressed = await compressImage(file, 200, 0.6);
        const updated = [...(imageChoices || [])];
        updated[i] = { ...updated[i], image: compressed };
        setImageChoices(updated);
      } catch (err) {
        toast.error("Failed to compress image");
      }
    };
    input.click();
  };

  const removeImage = (i) => {
    const updated = [...(imageChoices || [])];
    updated[i] = { ...updated[i], image: "" };
    setImageChoices(updated);
  };

  return (
    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Skip Option</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Allow visitors to skip</Typography>
          </Box>
          <Switch checked={!!skipOption} onChange={(e) => setSkipOption(e.target.checked)} size="small" />
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Display Style</Typography>
        <ToggleButtonGroup value={style || "button"} exclusive size="small" fullWidth
          onChange={(_, v) => v && setStyle(v)}
          sx={{ mb: 2, "& .MuiToggleButton-root": { textTransform: "none", fontSize: 11, fontWeight: 600, flex: 1 } }}>
          <ToggleButton value="button">Button</ToggleButton>
          <ToggleButton value="dropdown">Dropdown</ToggleButton>
          <ToggleButton value="list">List View</ToggleButton>
        </ToggleButtonGroup>

        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Layout Direction</Typography>
        <ToggleButtonGroup value={flexDirection || "column"} exclusive size="small" fullWidth
          onChange={(_, v) => v && setFlexDirection(v)}
          sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: 11, fontWeight: 600, flex: 1 } }}>
          <ToggleButton value="row">↔ Horizontal</ToggleButton>
          <ToggleButton value="column">↕ Vertical</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1.5, color: "#1f2937" }}>Options</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
          {optionsList.map((opt, i) => (
            <Box key={i} sx={{ display: "flex", gap: 0.8, alignItems: "center" }}>
              <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: `${accentColor}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: accentColor }}>{i + 1}</Typography>
              </Box>
              <TextField fullWidth size="small" placeholder={`Option ${i + 1}`}
                value={optText(opt)}
                onChange={(e) => handleChangeOption(i, e.target.value)}
                sx={accentText(accentColor)} />
              <IconButton size="small" onClick={() => handleDeleteOption(i)} sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 1.5, cursor: "pointer", color: accentColor, "&:hover": { color: accentColor, filter: "brightness(0.85)" } }}
          onClick={handleAddOption}>
          <AddIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Add Option</Typography>
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1.5, color: "#1f2937" }}>Image Choices</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          {optionsList.map((opt, i) => (
            <Box key={i} sx={{ position: "relative", width: 80, textAlign: "center" }}>
              <Box
                onClick={() => handleImageUpload(i)}
                sx={{
                  width: 72, height: 72, mx: "auto", borderRadius: 2,
                  border: `1.5px dashed ${imageChoices?.[i]?.image ? accentColor : "#d0d0d0"}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", overflow: "hidden", bgcolor: "#fafafa", transition: "all 0.2s ease",
                  "&:hover": { borderColor: accentColor, transform: "scale(1.04)" },
                }}
              >
                {imageChoices?.[i]?.image ? (
                  <img src={imageChoices[i].image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <AddPhotoAlternateIcon sx={{ color: "#bbb", fontSize: 24 }} />
                )}
              </Box>
              {imageChoices?.[i]?.image && (
                <IconButton size="small" onClick={() => removeImage(i)}
                  sx={{ position: "absolute", top: -6, right: -6, bgcolor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", width: 20, height: 20, p: 0, "&:hover": { bgcolor: "#fee2e2" } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 12 }} />
                </IconButton>
              )}
              <Typography sx={{ fontSize: 10, mt: 0.5, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {optText(opt) || `Option ${i + 1}`}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={glassCard}>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ "&.Mui-focused": { color: accentColor } }}>Default Selected</InputLabel>
          <Select
            value={defaultSelected ?? ""}
            label="Default Selected"
            onChange={(e) => setDefaultSelected(e.target.value === "" ? null : Number(e.target.value))}
            sx={{ borderRadius: 2, bgcolor: "#fff", fontSize: 12, "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.1)" }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: accentColor } }}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {optionsList.map((opt, i) => (
              <MenuItem key={i} value={i}>{optText(opt) || `Option ${i + 1}`}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={glassCard}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Shuffle Options</Typography>
              <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Randomize the option order for each visitor</Typography>
            </Box>
            <Switch size="small" checked={!!shuffleOptions} onChange={(e) => setShuffleOptions(e.target.checked)} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Enable "Other" Option</Typography>
              <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Let visitors type a custom answer</Typography>
            </Box>
            <Switch size="small" checked={!!otherOption} onChange={(e) => setOtherOption(e.target.checked)} />
          </Box>
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Error Message</Typography>
        <TextField fullWidth placeholder="Please enter a valid answer" value={errorMessage || ""} size="small"
          onChange={(e) => setErrorMessage(e.target.value)}
          sx={accentText(accentColor)} />
      </Box>
    </Box>
  );
};

export default SingleChoiceTab;
